<template>
  <div class="main-page">
    <template v-if="showHeader">
      <section class="page-section mb-3">
        <div class="container-fluid">
          <div class="grid justify-content-between">
            <div class="col">
              <div class="">
                <div class="surface-100 p-3 text-lg font-bold">
                  Transactions On Mobile App
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </template>
    <section class="page-section">
      <div class="container-fluid">
        <div class="grid">
          <div class="col comp-grid">
            <div class="">
              <div class="flex align-items-center justify-content-around">
                <div v-if="searchText" :class="filterTagClass">
                  Search
                  <Chip
                    class="font-medium px-2 py-1"
                    removable
                    @remove="clearSearch()"
                    >{{ searchText }}</Chip
                  >
                </div>
              </div>
              <div class="">
                <div>
                  <template v-if="showBreadcrumbs && $route.query.tag">
                    <Breadcrumb
                      :home="{ icon: 'pi pi-home', to: '/payment_history' }"
                      :model="pageBreadCrumb"
                    />
                  </template>
                  <div class="grid justify-content-start">
                    <!-- Master Page Start -->
                    <div class="col-12">
                      <div class="" :class="{ card: !isSubPage }">
                        <!-- page records template -->
                        <div class="page-records" v-if="!loading">
                          <DataTable
                            :lazy="true"
                            :loading="loading"
                            :value="records"
                            dataKey="payment_history_id"
                            @sort="onSort($event)"
                            class=""
                            :showGridlines="false"
                            :rowHover="true"
                            responsiveLayout="stack"
                          >
                            <Column
                              field="transaction_id"
                              header="Trans. ID"
                              :sortable="true"
                            >
                              <template #body="{ data }">
                                {{ data.transaction_id }}
                              </template>
                            </Column>
                            <Column field="amount" header="Amount">
                              <template #body="{ data }">
                                {{ data.amount }}
                              </template>
                            </Column>
                            <Column field="msisdn" header="Msisdn">
                              <template #body="{ data }">
                                {{ data.msisdn }}
                              </template>
                            </Column>
                            <Column field="payment_status" header="Status">
                              <template #body="{ data }">
                                <span
                                  v-if="data.payment_status == 'success'"
                                  class="font-medium text-green-500"
                                  >{{
                                    $utils.capitalize(data.payment_status)
                                  }}</span
                                >
                                <span
                                  v-else-if="data.payment_status == 'failed'"
                                  class="font-medium text-pink-500"
                                  >{{
                                    $utils.capitalize(data.payment_status)
                                  }}</span
                                >
                                <span
                                  v-else
                                  class="font-medium text-yellow-500"
                                  >{{
                                    $utils.capitalize(data.payment_status)
                                  }}</span
                                >
                              </template>
                            </Column>
                            <Column
                              field="date_created"
                              header="Date Created"
                              :sortable="true"
                            >
                              <template #body="{ data }">
                                {{ $utils.humanDatetime(data.date_created) }}
                              </template>
                            </Column>
                            <Column
                              headerStyle="width: 3em"
                              headerClass="text-center"
                            >
                              <template #body="{ data }">
                                <SplitButton
                                  class="p-button p-button-raised p-button-text p-button-sm"
                                  :model="getActionMenuModel(data)"
                                >
                                  <i></i>
                                </SplitButton>
                              </template>
                            </Column>
                          </DataTable>
                        </div>
                        <!-- page loading indicator -->
                        <template v-if="loading">
                          <div>
                            <DataTable
                              :value="[1, 2, 3]"
                              responsiveLayout="scroll"
                            >
                              <Column>
                                <template #body>
                                  <Skeleton />
                                </template>
                              </Column>
                              <Column>
                                <template #body>
                                  <Skeleton borderRadius="16px" />
                                </template>
                              </Column>
                              <Column>
                                <template #body>
                                  <Skeleton />
                                </template>
                              </Column>
                              <Column style="width: 3rem">
                                <template #body>
                                  <Skeleton size="2rem" />
                                </template>
                              </Column>
                            </DataTable>
                          </div>
                        </template>
                        <!-- end of page loading indicator-->
                        <!-- Empty record -->
                        <template v-if="pageReady && !records.length">
                          <div
                            class="p-3 my-3 text-500 text-lg font-medium text-center"
                          >
                            No record found
                          </div>
                        </template>
                        <!-- end of empty record-->
                        <!-- pagination component-->
                        <template v-if="showFooter">
                          <div class="">
                            <div class="flex justify-content-between">
                              <div class="flex-grow-0">
                                <div class="flex"></div>
                              </div>
                              <div class="flex-grow-1">
                                <div
                                  v-if="pageReady && totalPages > 1"
                                  class="flex justify-content-center"
                                >
                                  <div class="m-2">
                                    <Button
                                      :disabled="pagination.page <= 1"
                                      label="Previous"
                                      @click="loadPreviousPage()"
                                      icon="pi pi-arrow-left"
                                    />
                                  </div>
                                  <div class="m-2">
                                    <Button
                                      :disabled="pagination.page >= totalPages"
                                      label="Next"
                                      @click="loadNextPage()"
                                      icon="pi pi-arrow-right"
                                      icon-pos="right"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </template>
                        <!-- end of pagination component-->
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
<script setup>
import { reactive, toRefs, onMounted } from "vue";
import { useApp } from "@/composables/app.js";
import { useAuth } from "@/composables/auth";
import { useListPage } from "@/composables/listpage.js";
const props = defineProps({
  primaryKey: {
    type: String,
    default: "payment_history_id",
  },
  pageName: {
    type: String,
    default: "payment_history",
  },
  routeName: {
    type: String,
    default: "payment_historysublist",
  },
  apiPath: {
    type: String,
    default: "payment_history/sublist",
  },
  paginate: {
    type: Boolean,
    default: true,
  },
  showHeader: {
    type: Boolean,
    default: true,
  },
  showFooter: {
    type: Boolean,
    default: true,
  },
  showBreadcrumbs: {
    type: Boolean,
    default: true,
  },
  exportButton: {
    type: Boolean,
    default: true,
  },
  importButton: {
    type: Boolean,
    default: false,
  },
  multiCheckbox: {
    type: Boolean,
    default: false,
  },
  emptyRecordMsg: {
    type: String,
    default: "No record found",
  },
  titleBeforeDelete: {
    type: String,
    default: "Delete record",
  },
  msgBeforeDelete: {
    type: String,
    default: "Are you sure you want to delete this record?",
  },
  msgAfterDelete: {
    type: String,
    default: "Record deleted successfully",
  },
  page: {
    type: Number,
    default: 1,
  },
  limit: {
    type: Number,
    default: 3,
  },
  mergeRecords: {
    // for infinite loading
    type: Boolean,
    default: false,
  },
  search: {
    type: String,
    default: "",
  },
  fieldName: null,
  fieldValue: null,
  sortBy: {
    type: String,
    default: "",
  },
  sortType: {
    type: String,
    default: "", //desc or asc
  },
  isSubPage: {
    type: Boolean,
    default: false,
  },
  filterTagClass: {
    type: String,
    default:
      "surface-card p-2 text-500 flex-grow-1 text-center m-1 mb-3 flex-grow-1 text-center",
  },
});
const app = useApp();
const auth = useAuth();
const filters = reactive({});
//init list page hook
const page = useListPage(props, filters);
//page state
const {
  totalRecords, // total records from api - Number
  recordCount, // current record count - Number
  loading, // Api loading state - Boolean
  selectedItems, // Data table selected items -Array
  pagination, //Pagination object - Object
  searchText, // search text - String
  pageReady, // api data loaded successfully
} = toRefs(page.state);
//page computed propties
const {
  records, // page record from store - Array
  apiUrl, // current api path - URL
  currentRecord, // master detail selected record - Object
  pageBreadCrumb, // get page navigation paths - Object
  canLoadMore, // if api has more data for loading - Boolean
  finishedLoading, // if api has finished loading - Boolean
  totalPages, // total number of pages from api - Number
  recordsPosition, // current record position from api - Number
} = page.computedProps;
//page methods
const {
  load, // load data from api
  reload, // reset pagination and load data
  loadNextPage, // load next page
  loadPreviousPage, // load previous page
  exportPage, // export page records - args = (exportFormats, apiUrl, pageName)
  clearSearch, // clear input search
  debounce, // debounce input search
  onSort, // reset page number before sort from api
  deleteItem, // delete item by id or selected items - args = (id) or (selectedItems)
  setCurrentRecord, // master detail set current record
  isCurrentRecord, // master detail
  removeFilter, // remove page filter item - args = (filter.propertyname)
  getFilterLabel, // get filter item display label - args = (filter.propertyname)
  filterHasValue, //check if filter item has value - args = (filter.propertyname)
  importComplete, // reload page after data import
} = page.methods;
function getActionMenuModel(data) {
  return [
    {
      label: "View",
      command: (event) => {
        app.openPageDialog({
          page: "payment_history/view",
          url: `/payment_history/view/${data.payment_history_id}`,
          closeBtn: true,
          position: "bottom",
        });
      },
      icon: "pi pi-eye",
      visible: auth.canView("payment_history/view"),
    },
  ];
}
onMounted(() => {
  const pageTitle = "Payment History";
  app.setPageTitle(props.routeName, pageTitle);
  load();
});
</script>
<style scoped></style>
